import { Ref } from 'vue';
import { useUserStore } from '@/entities/User/@x/Branch';
import { useTheme } from '@/shared/lib';
import type { Branch } from '@/entities/Branch/model/branch.types';
import {
  clearSelectedBranch,
  getCurrentSelectedBranch,
  setCurrentSelectedBranch,
} from './selectedBranchStorage';
import { selectBranchForAppearance } from './selectBranchForAppearance';

function useBranchTheme() {
  const { setTheme } = useTheme();

  const selectBranchForTheme = (chosenBranch?: Ref<string, string>): Branch | null => {
    try {
      const userStore = useUserStore();
      const { branch, shouldPersist } = selectBranchForAppearance({
        chosenBranchId: chosenBranch?.value,
        accessibleBranches: userStore.accessibleBranches,
        branches: userStore.branches,
        savedBranchId: getCurrentSelectedBranch(),
      });

      if (branch && shouldPersist) {
        setCurrentSelectedBranch(branch.id.toString());
      }

      return branch;
    } catch {
      return null;
    }
  };

  const applyBranchTheme = (branch?: Ref<string, string>) => {
    const selectedBranch = selectBranchForTheme(branch);

    if (
      selectedBranch &&
      selectedBranch.colorPrimary &&
      selectedBranch.colorSecondary &&
      selectedBranch.colorText
    ) {
      const hasDarkColors =
        selectedBranch.colorPrimaryDark &&
        selectedBranch.colorSecondaryDark &&
        selectedBranch.colorTextDark;

      setTheme({
        primary: selectedBranch.colorPrimary,
        secondary: selectedBranch.colorSecondary,
        text: selectedBranch.colorText,
        ...(hasDarkColors
          ? {
              dark: {
                primary: selectedBranch.colorPrimaryDark as string,
                secondary: selectedBranch.colorSecondaryDark as string,
                text: selectedBranch.colorTextDark as string,
              },
            }
          : {}),
      });
    }

    return selectedBranch;
  };

  return {
    selectBranchForTheme,
    applyBranchTheme,
    getCurrentSelectedBranch,
    setCurrentSelectedBranch,
    clearSelectedBranch,
  };
}

export default useBranchTheme;
